import moment from 'moment';
import * as i18next from 'i18next';
import * as api from '../api';
import * as flights from '../flights';
import * as payment from '../payment';
import * as utils from '../utils';
import { Filters } from 'core/filters';
import { G2S } from '../g2s';

export interface ProductConfirmationData {
    product?: flights.ProductModel;
    popup: api.ProductPopup;
}

export interface ProductDiscountConfirmationData {
    productDiscount?: flights.ProductModel;
    popup: api.ProductPopup;
}

export interface ProductServiceBrand {
    services?: api.ProductBrand[];
    popup: api.ProductPopup;
}

export interface BaggageConfirmationData {
    baggageData?: flights.ConfirmBaggageData | null;
    popup: api.ProductPopup;
}

export interface ConfirmBaggageData {
    baggage: string;
    price: string;
}

const NO_BAGGAGE_GUID = '00000000-0000-0000-0000-000000000000';

export class ProductPopupModel implements api.ProductPopup {
    backgroundColor: number;
    brandedKitId: number | null;
    buttonAction1: string;
    buttonAction2: string;
    buttonAction3: string;
    buttonText1: string;
    buttonText2: string;
    buttonText3: string;
    buttonColor1: string;
    buttonColor2: string;
    buttonColor3: string;
    buttonIcon1?: string;
    buttonIcon2?: string;
    buttonIcon3?: string;
    buttonStyle1: string;
    buttonStyle2: string;
    buttonStyle3: string;
    discountId?: number;
    displayType?: any;
    emTextColor?: string;
    isCloseIconVisible: boolean;
    kitId: number;
    markerText: string;
    popupId: number;
    position: api.ProductPopupPosition;
    productId: number;
    productName?: string | null;
    reverse?: boolean;
    reservationPriceText?: string | null;
    secondProductId?: number;
    targetProductId: number;
    text: string;
    type: api.ProductPopupType;
    userInteractionDone: boolean;
    popupTypeNumber: number;
    crossedOutPrice?: number | null;
    timerText?: string | null;
    timerDuration?: number | null;

    constructor(sourceProductPopup: api.ProductPopup) {
        this.backgroundColor = sourceProductPopup.backgroundColor;
        this.brandedKitId = sourceProductPopup.brandedKitId;
        this.buttonText1 = sourceProductPopup.buttonText1;
        this.buttonText2 = sourceProductPopup.buttonText2;
        this.buttonText3 = sourceProductPopup.buttonText3;
        this.buttonAction1 = sourceProductPopup.buttonAction1;
        this.buttonAction2 = sourceProductPopup.buttonAction2;
        this.buttonAction3 = sourceProductPopup.buttonAction3;
        this.buttonColor1 = sourceProductPopup.buttonColor1;
        this.buttonColor2 = sourceProductPopup.buttonColor2;
        this.buttonColor3 = sourceProductPopup.buttonColor3;
        this.buttonIcon1 = sourceProductPopup.buttonIcon1;
        this.buttonIcon2 = sourceProductPopup.buttonIcon2;
        this.buttonIcon3 = sourceProductPopup.buttonIcon3;
        this.buttonStyle1 = sourceProductPopup.buttonStyle1;
        this.buttonStyle2 = sourceProductPopup.buttonStyle2;
        this.buttonStyle3 = sourceProductPopup.buttonStyle3;
        this.discountId = sourceProductPopup.discountId;
        this.emTextColor = sourceProductPopup.emTextColor;
        this.isCloseIconVisible = sourceProductPopup.isCloseIconVisible;
        this.kitId = sourceProductPopup.kitId;
        this.markerText = sourceProductPopup.markerText;
        this.popupId = sourceProductPopup.popupId;
        this.position = sourceProductPopup.position;
        this.productId = sourceProductPopup.productId;
        this.productName = sourceProductPopup.productName;
        this.reservationPriceText = sourceProductPopup.reservationPriceText;
        this.secondProductId = sourceProductPopup.secondProductId;
        this.targetProductId = sourceProductPopup.targetProductId;
        this.text = sourceProductPopup.text;
        this.type = sourceProductPopup.type;
        this.userInteractionDone = false;
        this.displayType = sourceProductPopup.displayType;
        this.crossedOutPrice = sourceProductPopup.crossedOutPrice;
        this.timerText = sourceProductPopup.timerText;
        this.timerDuration = sourceProductPopup.timerDuration;

        switch (sourceProductPopup.type){
            case 'Product':
                this.popupTypeNumber = 0;
                break;
            case 'FlightBaggage':
                this.popupTypeNumber = 1;
                break;
            case 'Discount':
                this.popupTypeNumber = 2;
                break;
            case 'Gift':
                this.popupTypeNumber = 3;
                break;
        }

        // Test reverse order for buttons
        const testKits = [83, 84, 85, 86, 87, 88, 89, 90, 91, 92];

        if (testKits.indexOf(this.kitId) >= 0) {
            this.reverse = true;
        }
    }
}

export class SeatMapModel {
    columns: number;
    flightCode: string;
    rows: any;
    rowsCount: number;
    seatBrands: api.SeatBrand[];
    segmentFlightCode: string;

    constructor(sourceSeatMap: api.SeatMap) {
        this.columns = sourceSeatMap.columns;
        this.flightCode = sourceSeatMap.flightCode;
        this.rows = sourceSeatMap.rows;
        this.rowsCount = sourceSeatMap.rowsCount;
        this.seatBrands = sourceSeatMap.seatBrands;
        this.segmentFlightCode = sourceSeatMap.segmentFlightCode;
    }
}

export class ProductModel {
    product: api.Product;
    favorite: boolean;
    label = '';
    selected: boolean;
    backgroundColor: string | undefined;
    selectedOptions: string[];
    choiceType: api.ProductChoiceType;
    needToSelect = false;
    
    constructor(sourceProduct: api.Product) {
        this.product = sourceProduct;
        this.backgroundColor = utils.String.camelToKebab(sourceProduct.backgroundColor);
        this.selected = sourceProduct.selectedByDefault;
        this.selectedOptions = ['', '', '']; // Support 3 options
        this.choiceType = api.ProductChoiceType.notSet;

        if (this.product.externalId === 'MegoPackageAllInclusive') {
            this.favorite = true;
        }

        if (this.product.acceptOrDenyRequired) {
            this.needToSelect = true;
        }
        //switch (this.product.externalId) {
        //    case 'GO2SEE-NS-BG-BUSINESS':
        //    case 'GO2SEE-NS-BG-ZR':
        //    case 'GO2SEE-NS-BG-ZR-ECONOM':
        //    case 'GO2SEE-NS-BG-ZR-BUSINESS':
        //        this.label = 'Безопасное путешествие';
        //        break;
        //    case 'GO2SEE-NSP-TRAVEL':
        //        this.label = 'Спокойствие';
        //        break;
        //    case 'Go2SeeNotification':
        //        this.label = 'Удобно!';
        //        break;
        //    case 'Go2SeeRefund':
        //        this.label = 'Защита денег';
        //        break;
        //    case 'Go2SeeServiceFlightSickRefund':
        //        this.label = 'Забота о здоровье';
        //        break;
        //    case 'Go2SeeServiceFlightFlexBooking':
        //    case 'Go2SeeRefundGuaranty':
        //        this.label = 'Легкость изменений';
        //        break;
        //    case 'Go2SeeServiceFreeSupport':
        //        this.label = 'Все включено';
        //        break;
        //    case 'MegoPackageAllInclusive':
        //        this.label = 'Выгодно + подарок!';
        //        break;
        //}
    }
}

export class PassengerValidationConstraints {
    allowCyrillicDocumentNumber: boolean;
    allowCyrillicName: boolean;
    citizenshipConstraints: api.CitizenshipConstraint[];
    countriesCitizenshipConstrain: any;
    isRequiredCitizenshipConstrains: boolean;
    orderFormNewDocumentNumberRuValidationEnabled: boolean;
    maxDateOfBirth: moment.Moment;
    middleNameRequired: boolean;
    mileCardVisible: boolean;
    minDateOfBirth: moment.Moment;
    minDocumentValidTill: moment.Moment;
    requiredCitizenshipCountries: number[];
    documents: any[];
    documentPassportForeign: any[];
    documentPassportRu: any[];
    documentOtherDocument: any[];
    documentBirthCertificate: any[];
}

export class PassengerModel {
    index = 0;
    type: api.PassengerType | null = null;
    firstName = '';
    middleName = '';
    lastName = '';
    birthday = ''; // string used because of timezone problems with Date
    gender = '';
    card = '';
    cardAirlineId = '';
    selectedFlightSeats: any = {};
    seatWithInfant = false;
    citizenship = 0;
    allowCyrillicName = true;
    documentType = '';
    documentNumber = '';
    documentValidTillRequired: boolean;
    documentValidTillVisible: boolean;
    documentLastNameRequired: boolean;
    documentLastNameVisible: boolean;
    documentValidTill = ''; // string used because of timezone problems with Date
    documentValidTillEnabled = true;
    baggageOptions: string[] = [];

    validationConstraints: PassengerValidationConstraints;

    constructor() {
        this.validationConstraints = new PassengerValidationConstraints();
    }

    checkDocType(countryCrossing: string, citizenship: number, type: api.PassengerType | null) {
        // citizenship === 0 приходит для sirena 30
        if (citizenship === 168 || citizenship === 0) {
            if (type === 'adult' && this.validationConstraints['documentPassportRu']) {
                if ((/[0-9IVX]{1,4}[а-яА-Я]{2}[0-9]{6}$/).test(this.documentNumber) && this.validationConstraints['documentBirthCertificate']) {
                    this.documentType = 'documentBirthCertificate';
                // } else if ((/[а-яА-Я]/).test(this.documentNumber) && this.validationConstraints['documentOtherDocument']) {
                //     this.documentType = 'documentOtherDocument';
                //     this.allowCyrillicName = true;
                } else {
                    if ((/^[0-9]{9}$/).test(this.documentNumber)) {
                        this.documentType = 'documentPassportForeign';
                        this.allowCyrillicName = false;
                    } else {
                        this.documentType = 'documentPassportRu';
                        this.allowCyrillicName = true;
                    }
                }
            } else {
                if (!this.validationConstraints['documentBirthCertificate'] || (/^[0-9]{9}$/).test(this.documentNumber) || countryCrossing.includes('AM')) {
                    this.documentType = 'documentPassportForeign';
                    this.allowCyrillicName = false;
                } else {
                    this.documentType = 'documentBirthCertificate';
                    this.allowCyrillicName = true;
                }
            }
        } else {
            this.documentType = 'documentPassportForeign';
            this.allowCyrillicName = false;
        }

        const constrainByDoc = this.validationConstraints[this.documentType] || this.validationConstraints['documentPassportForeign'];

        const docLastNameConstrains = constrainByDoc.constraints
            .find((constrain) => constrain.nameProperty === 'LastName');

        this.documentLastNameVisible = docLastNameConstrains.nameConstraint !== 'NotAllowed';
        this.documentLastNameRequired = docLastNameConstrains.nameConstraint === 'Required';

        const docPeriodConstrains = constrainByDoc.constraints
            .find((constrain) => constrain.nameProperty === 'ValidityPeriodOfDocument');

        this.documentValidTillVisible = docPeriodConstrains.nameConstraint !== 'NotAllowed';
        this.documentValidTillRequired = docPeriodConstrains.nameConstraint === 'Required';
    }

    isValid(): boolean {
        if (!this.gender) {
            return false;
        }
        if (!this.lastName) {
            return false;
        }
        if (!this.firstName) {
            return false;
        }
        if (!this.birthday) {
            return false;
        } else {
            const m = moment(this.birthday, 'YYYY-MM-DD');

            if (m.isValid()
                && (m.isBefore(this.validationConstraints.minDateOfBirth) || m.isAfter(this.validationConstraints.maxDateOfBirth))) {
                return false;
            }
        }
        if (!this.documentNumber) {
            return false;
        }
        if (this.documentValidTillEnabled) {
            if (!this.documentValidTill) {
                return false;
            } else {
                const m = moment(this.documentValidTill, 'YYYY-MM-DD');

                if (m.isValid()
                    && m.isBefore(this.validationConstraints.minDocumentValidTill)) {
                    return false;
                }
            }
        }
        // const regExpBirthCert = /^(?<s1>[0-9IVX]{1,4})(?<s2>[a-zA-Zа-яА-Я]{2})(?<n>[0-9]{6})$/;

        return true;
    }
}

export class ReservationModel {
    private maxPopupAmount = 5;
    private maxPopupAmountOnOneStep = 3;
    private currentPopupAmount = 0;

    contacts: ContactsModel = {
        email: '',
        phone: ''
    };

    // Applied coupon. Id used for GA, Guid used on booking.
    couponId: number | null = null;
    couponGuid: string | null = null;
    couponDiscountAmount = 0;
    couponDataExpiring: any = 0;
    couponDataActivate: any = null;
    couponProductId: number | null = null;
    couponDiscountAmountOld = 0;
    couponProduct: any | null = null;
    couponProductAdd: any | null = null;

    bookingSessionId: number | null = null;

    surchargeInfo: api.SurchargeInfo;
    passengers: PassengerModel[] = [];
    rawProducts: ProductModel[] = [];
    seatMaps: SeatMapModel[];
    flatSelectedSeats: {} = {};
    
    productPopups: ProductPopupModel[] = [];
    productPopupsDiscount: ProductPopupModel[] = [];

    needToSelectToGoToNextStep = false;

    flights: flights.FlightModel[] = [];
    trains: flights.TrainModel[] = [];
    trainCoaches: { [schemaId: string]: string } = {};

    markerOwner = '';

    termsAccepted = false;

    countries: api.Destination[] = [];

    payment: payment.PaymentModel;
    paymentData: payment.YandexPaymentData;

    selectedFlightId = '';
    // Initial flight selected by url
    defaultFlightId = '';

    baggageOptions: string[] = [];

    baggageChoiceType: api.BaggageChoiceType = api.BaggageChoiceType.notSet;
    brandChoiceType: api.FlightBrandChoiceType = api.FlightBrandChoiceType.notSet;
    brandGroups: flights.FlightGroupModel[] = [];
    brandedProductKits?: api.ProductBrand[];
    forwardKits: (api.ProductBrand & { wasShown?: boolean })[] = [];
    // Flag to check that process before booking will be made only once
    processedBeforeBooking = false;

    productExternalIdsForStep: {
        passengers: string[];
        payment: string[];
    } = {
        passengers: [
            'Go2SeeRefund'//, 'Go2SeeNotification'
            /*'GO2SEE-NSP-TRAVEL', 'GO2SEE-NS-BG-BUSINESS', 'GO2SEE-NS-BG-ZR', 'GO2SEE-NS-BG-ZR-ECONOM', 'GO2SEE-NS-BG-ZR-BUSINESS', 'ON_BG_NS500_185_290', 'ON_BG_ZV_NS500_250_390'*/
        ],
        payment: [
            'Go2SeeServiceFlightSickRefund'//, 'Go2SeeNotification'
        ]
    }

    additionalDocs = {
        TKP_AAM: [
            {
                text: i18next.t('title_offer'),
                link: '/d/mixvel/OfferAAM.pdf',
            },
            {
                
                text: i18next.t('title_additional_flight_info'),
                link: '/d/mixvel/AdditionalInfoAAM.pdf',
                
            },
        ],
    };

    packageRelatedProducts = {
        'Go2SeeRefund':[
            { externalId: 'MegoPackageAllInclusive', criterion: 'equal' },
            { externalId: 'MegoPackagePremium', criterion: 'popup' },
            { externalId: 'MegoPackageCalm', criterion: 'equal' }
        ],
        'Go2SeeNotification': [
            { externalId: 'MegoPackagePremium', criterion: 'popup' },
            { externalId: 'MegoPackageSuperSms', criterion: 'equal' },
            { externalId: 'MegoPackageAllInclusive', criterion: 'equal' }
        ],
        'Go2SeeServiceFreeSupport': [
            { externalId: 'MegoPackageOptimum', criterion: 'equal' },
            { externalId: 'MegoPackagePremium', criterion: 'equal' }
        ],
        'MegoPackageSuperSms': [
            { externalId: 'Go2SeeNotification', criterion: 'equal' },
            { externalId: 'MegoPackageAllInclusive', criterion: 'equal' },
            { externalId: 'MegoPackagePremium', criterion: 'equal' }
        ],
        'MegoPackageAllInclusive': [
            { externalId: 'Go2SeeServiceFlightFlexBooking', criterion: 'equal' },
            { externalId: 'Go2SeeRefund', criterion: 'equal' },
            { externalId: 'Go2SeeNotification', criterion: 'equal' },
            { externalId: 'MegoPackagePremium', criterion: 'equal' }
        ],
        // MegoPackageCalm продуктов нет на продакшне
        'MegoPackageCalm': [
            { externalId: 'GO2SEE-NS-BG-ZR', criterion: 'startsWith' },
            { externalId: 'Go2SeeRefund', criterion: 'equal' },
            { externalId: 'Go2SeeNotification', criterion: 'equal' }
        ],
        'MegoPackageOptimum': [
            { externalId: 'Go2SeeServiceFreeSupport', criterion: 'equal' }
        ],
        'MegoPackagePremium': [
            { externalId: 'Go2SeeRefund', criterion: 'equal' },
            { externalId: 'Go2SeeNotification', criterion: 'equal' },
            { externalId: 'Go2SeeServiceFreeSupport', criterion: 'equal' },
            { externalId: 'MegoSmsTicket', criterion: 'equal' },
            { externalId: 'MegoPackageSuperSms', criterion: 'equal' },
            { externalId: 'MegoPackageAllInclusive', criterion: 'equal' }
        ]
    }

    constructor(reservationDesc?: api.FlightBookingDetails) {
        if (reservationDesc) {
            this.bookingSessionId = reservationDesc.bookingSessionId;
            this.selectedFlightId = reservationDesc.selectedFlightUniqueId;
            this.defaultFlightId = reservationDesc.selectedFlightUniqueId;
            this.surchargeInfo = reservationDesc.surchargeInfo;
            
            this.brandedProductKits  = reservationDesc.brandedProductKits?.map(kit => {
                kit.needToSelect = kit.acceptOrDenyRequired;
                kit.products.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                return kit;
            });
            reservationDesc.flights.forEach(f => {
                this.flights.push(new flights.FlightModel(f, reservationDesc));
            });
            reservationDesc.trains.forEach(t => {
                this.trains.push(new flights.TrainModel(t));
            });
            if (reservationDesc.trainCoaches) {
                for (const schemaId in reservationDesc.trainCoaches) {
                    this.trainCoaches[schemaId.toUpperCase()] = reservationDesc.trainCoaches[schemaId];
                }
            }

            const documentValidTillEnabled = this.selectedFlight!.area !== 'RU';
            

            this.buildPassengers(
                reservationDesc.search.adults,
                api.PassengerType.adult,
                reservationDesc.defaultCitizenshipId,
                documentValidTillEnabled,
                this.buildPassengerConstraints(reservationDesc.constraints, api.PassengerType.adult)
            );

            this.buildPassengers(
                reservationDesc.search.children,
                api.PassengerType.child,
                reservationDesc.defaultCitizenshipId,
                documentValidTillEnabled,
                this.buildPassengerConstraints(reservationDesc.constraints, api.PassengerType.child)
            );

            this.buildPassengers(
                reservationDesc.search.babies,
                api.PassengerType.infant,
                reservationDesc.defaultCitizenshipId,
                documentValidTillEnabled,
                this.buildPassengerConstraints(reservationDesc.constraints, api.PassengerType.infant)
            );

            this.rawProducts = [];

            reservationDesc.products.forEach(p => {
                this.rawProducts.push(new ProductModel(p));
            });

            this.seatMaps = [];

            reservationDesc.flights.forEach(f => {
                f.seatMaps?.forEach(s => {
                    this.seatMaps.push(new SeatMapModel(s));
                });
            });

            reservationDesc.productPopups.forEach(pp => {
                this.productPopups.push(new ProductPopupModel(pp));
            });

            reservationDesc.productPopups.forEach(pp => {
                if (pp.type === 'Discount') {
                    this.productPopupsDiscount.push(new ProductPopupModel(pp));
                }
            });

            this.countries = reservationDesc.countries;
            this.payment = new payment.PaymentModel(reservationDesc.paymentTypes);
            this.resetAllSelectedBaggage();

            // For UI depending on marker owner
            this.markerOwner = utils.Cookies.getMarkerOwner();

            // Init brand groups for MultiFR brands.
            if (this.defaultFlight?.flightKind === 'Multi') {
                const flightIds = this.defaultFlightId.split('_');

                if (flightIds.length > 1) {
                    for (let i = 0; i < this.flights.length; i++) {
                        const f = this.flights[i];

                        if (f.uniqueId !== this.defaultFlightId && f.flightKind !== 'Multi') {
                            const fg = this.brandGroups.find(g => g.brandGroupGuid === f.brand.brandGroupGuid);

                            if (fg) {
                                fg.flights.push(f);
                            }
                            else {
                                const bg = new flights.FlightGroupModel();

                                bg.brandGroupGuid = f.brand.brandGroupGuid;
                                bg.selectedFlightId = f.uniqueId;
                                bg.defaultFlightId = f.uniqueId;
                                bg.flights = [f];

                                this.brandGroups.push(bg);
                            }
                        }
                    }

                    // Init brandGroup default/selected flight from defaultFlightId
                    flightIds.forEach((id) => {
                        this.brandGroups.forEach((brandGroup) => {
                            brandGroup.flights.forEach((flight) => {
                                if (flight.uniqueId == id) {
                                    brandGroup.defaultFlightId = id;
                                    brandGroup.selectedFlightId = id;
                                }
                            });
                        });
                    });
                }
                else {
                    console.error('Could not parse flight ids from defaultFlightId ' + this.defaultFlightId);
                }
            }
        }
    }

    private get selectedCustomBaggageIds(): string[] {
        let selectedIds = this.baggageOptions.filter(id => id && id != '' && id != NO_BAGGAGE_GUID);
        const result: string[] = [];

        this.passengers.forEach((passenger) => {
            selectedIds = selectedIds.concat(passenger.baggageOptions.filter(id => id && id != '' && id != NO_BAGGAGE_GUID));
        });
        selectedIds.forEach((id) => {
            if (!result.some(resultId => resultId == id)) {
                result.push(id);
            }
        });

        return result;
    }
    
    public getSelectedCustomBaggageIds(){
        let selectedIds = this.baggageOptions.filter(id => id && id != '' && id != NO_BAGGAGE_GUID);
        const result: string[] = [];

        this.passengers.forEach((passenger) => {
            selectedIds = selectedIds.concat(passenger.baggageOptions.filter(id => id && id != '' && id != NO_BAGGAGE_GUID));
        });
        selectedIds.forEach((id) => {
            if (!result.some(resultId => resultId == id)) {
                result.push(id);
            }
        });

        return result;
    }

    public hasSeatMaps(){
        let result = false;
        for (let i = 0; i < this.flights.length; i++) {
            if (this.flights[i].seatMaps?.length){
                result = true;
                break;
            } else {
                result = false;
            }
            
        }

        return result;
    }

    get hasBrands(): boolean {
        return this.flights && this.flights.length > 1;
    }

    get additionalDocsFlat(): any {
        const localAdditionalDocs = this.additionalDocs;
        const allDocs = this.flights.map(flight => flight.additionalOfferKeys).flat();
        const flatDocsList = allDocs
            .map(key => localAdditionalDocs[key as keyof typeof localAdditionalDocs])
            .filter(Boolean)
            .flat(1);

        const uniqFlatDocsList = Array.from(new Set(flatDocsList));

        return uniqFlatDocsList;
    }

    get hasFlightSeatMaps(): boolean {
        let found = false;
        
        for (let i = 0; i < this.flights.length; i++) {
            if (this.flights[i].uniqueId == this.selectedFlightId && this.flights[i].seatMaps?.length) {
                for (let j = 0; j < this.flights[i].legs.length; j++) {
                    for (let ji = 0; ji < this.flights[i].legs[j].segments.length; ji++) {
                        found = this.hasSegmentAvaliableSeatMaps(this.flights[i].legs[j].segments[ji].flightCode);
                        if (found) return found;
                    }   
                }
            }
        }
        
        return found;
    }

    // Check if MultiFR brands should be displayed and used.
    get hasBrandGroups(): boolean {
        return this.hasBrands && this.defaultFlight?.flightKind === 'Multi' && this.brandGroups.length > 0;
    }

    get bookingNotification(): string {
        // if (this.selectedFlight
        //     && this.selectedFlight.destinations.find(d => d.country.iataCode == 'TR')) {
        //     return i18next.t('label_notification_covid19');
        // }
        return '';
    }

    get customBaggagePrice(): number {
        let result = 0;

        if (this.selectedFlight
            && this.selectedFlight.customBaggage
            && this.selectedFlight.customBaggage.options
            && this.selectedFlight.customBaggage.options.length) {
            this.baggageOptions.forEach((baggageOption, baggageOptionIndex) => {
                if (baggageOption) {
                    const baggageItem = this.selectedFlight!.customBaggage.options![baggageOptionIndex]
                        .baggageOptions.find(x => x.id == baggageOption);

                    if (baggageItem && baggageItem.price) {
                        result += baggageItem.price;
                    }
                }
            });
            this.passengers.forEach((passenger) => {
                passenger.baggageOptions.forEach((baggageOption, baggageOptionIndex) => {
                    if (baggageOption) {
                        const baggageItem = this.selectedFlight!.customBaggage.options![baggageOptionIndex]
                            .baggageOptions.find(x => x.id == baggageOption);

                        if (baggageItem && baggageItem.price) {
                            result += baggageItem.price;
                        }
                    }
                });
            });
        }

        return result;
    }

    get flightRegistrationRowItems() {
        return [
            {
                label: i18next.t('label_no_matter'),
                value: ''
            },
            {
                label: i18next.t('label_flight_row_start'),
                value: 'start'
            },
            {
                label: i18next.t('label_flight_row_middle'),
                value: 'middle'
            },
            {
                label: i18next.t('label_flight_row_tail'),
                value: 'tail'
            }
        ];
    }

    get flightRegistrationSeatItems() {
        return [
            {
                label: i18next.t('label_no_matter'),
                value: ''
            },
            {
                label: i18next.t('label_flight_seat_window'),
                value: 'window'
            },
            {
                label: i18next.t('label_flight_seat_middle'),
                value: 'middle'
            },
            {
                label: i18next.t('label_flight_seat_aisle'),
                value: 'aisle'
            }
        ];
    }

    get selectedFlight() {
        if (this.selectedFlightId) {
            return this.flights.find(flight => flight.uniqueId === this.selectedFlightId) || null;
        }
        return null;
    }

    get selectedFlightBillingPrice(): number {
        // Override flight price for multiFR brands.
        if (this.hasBrandGroups) {
            return this.getBrandGroupsBillingPrice();
        }
        else {
            if (this.selectedFlightId) {
                const f = this.flights.find(flight => flight.uniqueId === this.selectedFlightId);

                if (f) {
                    return f.billingPrice;
                }
            }
        }

        return 0;
    }

    get selectedFlightBasePrice(): number {
        // Override flight price for multiFR brands.
        if (this.hasBrandGroups) {
            return this.getBrandGroupsBasePrice();
        }
        else {
            if (this.selectedFlightId) {
                const f = this.flights.find(flight => flight.uniqueId === this.selectedFlightId);

                if (f) {
                    return f.basePrice;
                }
            }
        }

        return 0;
    }

    get selectedFlightBrandName(): string {
        let result = '';

        if (this.hasBrandGroups) {
            this.brandGroups.forEach((brandGroup) => {
                const f = this.flights.find(f => f.uniqueId == brandGroup.selectedFlightId);

                if (f && f.brand) {
                    if (result) {
                        result += ' + ';
                    }

                    result += f.brand.nameUI;
                }
            });
        }
        else {
            if (this.selectedFlight && this.selectedFlight.brand) {
                result = this.selectedFlight.brand.nameUI;
            }
        }

        return result;
    }

    get selectedFlightFee(): number {
        // Override flight price for multiFR brands.
        if (this.hasBrandGroups) {
            return this.getBrandGroupsFee();
        }
        else {
            if (this.selectedFlightId) {
                const f = this.flights.find(flight => flight.uniqueId === this.selectedFlightId);

                if (f) {
                    return f.fee;
                }
            }
        }

        return 0;
    }

    get defaultFlight() {
        return this.flights.find(flight =>
            flight.uniqueId === this.defaultFlightId);
    }

    get selectedFlightIdForProducts() {
        if (this.brandGroups.length > 0) {
            const ids = this.brandGroups.map(g => parseInt(g.selectedFlightId));
            ids.sort((a, b) => a - b);
            return ids.join('_');
        } else {
            return this.selectedFlightId;
        }
    }

    get products(): ProductModel[] {
        const result = {};
        let productDiscount: ProductModel | null = null;

        for (const product of this.rawProducts) {
            if (product.product.flightId === this.selectedFlightIdForProducts || !product.product.flightId) {
                if (product.product.discountRuleId) {
                    productDiscount = product;
                } else if (!result[product.product.id]) {
                    result[product.product.id] = product;
                }
            }
        }

        const resultArray = <ProductModel[]>Object.values(result);

        if (productDiscount) {
            resultArray.push(productDiscount);
        }

        return resultArray;
    }

    get selectedProducts(): ProductModel[] {
        return this.products.filter(p => p.selected);
    }

    get passengersForBaggage(): flights.PassengerModel[] {
        return this.passengers.filter(passenger =>
            passenger.type === api.PassengerType.adult
            || passenger.type === api.PassengerType.child
        );
    }

    get productsOfTypeInsurance(): ProductModel[] {
        return this.products.filter(p => p.product.saleType === api.ProductSaleType.bookingForm
            && p.product.type === api.ProductType.insurance);
    }

    get productsOfYandex(): ProductModel[] {
        return this.products.filter(p => p.product.saleType === api.ProductSaleType.yandex
            && p.product.type === api.ProductType.package);
    }

    get productsOfTypeService(): ProductModel[] {
        return this.products.filter(p => p.product.saleType === api.ProductSaleType.bookingForm
            && p.product.type === api.ProductType.service);
    }

    get productsOfTypePackage(): ProductModel[] {
        return this.products.filter(p => p.product.saleType === api.ProductSaleType.bookingForm
            && p.product.type === api.ProductType.package);
    }

    get productsOfGrouped(): ProductModel[] {
        return this.products.filter(p => p.product.saleType === api.ProductSaleType.bookingForm
            && p.product.displayType === api.ProductDisplayType.grouped);
    }

    get productsNotGrouped(): ProductModel[] {
        return this.products.filter(p => p.product.saleType === api.ProductSaleType.bookingForm
            && p.product.displayType !== api.ProductDisplayType.grouped);
    }

    get productsOfTypeServiceOrPackage(): ProductModel[] {
        return this.products.filter(p => p.product.saleType === api.ProductSaleType.bookingForm
            && (
                p.product.type === api.ProductType.service
                || p.product.type === api.ProductType.package
            )
            && p.product.displayType !== api.ProductDisplayType.grouped);
    }

    get hasSelectedBothCustomBaggage(): boolean {
        return this.checkCustomBaggageSelection(
            this.selectedCustomBaggageIds,
            this.getAvailableCustomBaggageItemsByFlightDirection(api.CustomBaggageFlightDirection.both)
        );
    }

    get hasSelectedOutwardCustomBaggage(): boolean {
        return this.checkCustomBaggageSelection(
            this.selectedCustomBaggageIds,
            this.getAvailableCustomBaggageItemsByFlightDirection(api.CustomBaggageFlightDirection.outward)
        );
    }

    get hasSelectedReturnCustomBaggage(): boolean {
        return this.checkCustomBaggageSelection(
            this.selectedCustomBaggageIds,
            this.getAvailableCustomBaggageItemsByFlightDirection(api.CustomBaggageFlightDirection.return)
        );
    }

    get hasSelectedAnyCustomBaggage() {
        return this.selectedCustomBaggageIds.length && this.selectedCustomBaggageIds.length > 0;
    }

    get hasOnlyTwoCustomBaggageLists() {
        if (this.selectedFlight) {
            if ((this.getCustomBaggageOptionsByFlightDirection(api.CustomBaggageFlightDirection.outward).length == 1
                && this.getCustomBaggageOptionsByFlightDirection(api.CustomBaggageFlightDirection.return).length == 1
                && this.getCustomBaggageOptionsByFlightDirection(api.CustomBaggageFlightDirection.both).length == 0)
                ||
                (this.getCustomBaggageOptionsByFlightDirection(api.CustomBaggageFlightDirection.outward).length == 1
                    && this.getCustomBaggageOptionsByFlightDirection(api.CustomBaggageFlightDirection.return).length == 0
                    && this.getCustomBaggageOptionsByFlightDirection(api.CustomBaggageFlightDirection.both).length == 1)
                ||
                (this.getCustomBaggageOptionsByFlightDirection(api.CustomBaggageFlightDirection.outward).length == 0
                    && this.getCustomBaggageOptionsByFlightDirection(api.CustomBaggageFlightDirection.return).length == 1
                    && this.getCustomBaggageOptionsByFlightDirection(api.CustomBaggageFlightDirection.both).length == 1)) {
                return true;
            }
        } else {
            return false;
        }
    }

    get passengersWithSeatCount() {
        const passengersCount = this.getPassengersCount();

        return passengersCount.adults + passengersCount.children;
    }

    get cleanSelectedProducts() {
        const internalProductIds: number[] = [];

        for (const product of this.selectedProducts) {
            if (product.product.internalProducts) {
                internalProductIds.push(...product.product.internalProducts.map(p => p.id));
            }
        }

        return this.selectedProducts.filter(p => !internalProductIds.includes(p.product.id));
    }

    get calculatedCleanSelectedProducts() {
        const cleanSelectedProducts: ProductModel[] = JSON.parse(JSON.stringify(this.cleanSelectedProducts));

        const groups: Record<string, any[]> = {};

        for (const product of cleanSelectedProducts) {
            if (product.product.internalProducts && product.product.internalProducts.length > 0) {
                for (const internalProduct of product.product.internalProducts) {
                    if (groups[internalProduct.id]) {
                        groups[internalProduct.id].push(internalProduct);
                    } else {
                        groups[internalProduct.id] = [internalProduct];
                    }
                }
            }
        }

        for (const id of Object.keys(groups)) {
            groups[id].sort((a, b) => a.billingPrice - b.billingPrice);
            
            for (const internalProduct of groups[id].slice(1)) {
                internalProduct.billingPrice = 0;
            }
        }

        for (let i = 0; i < cleanSelectedProducts.length; i++) {
            const product = cleanSelectedProducts[i];

            if (product.product.internalProducts && product.product.internalProducts.length > 0) {
                const totalInternalProductsCost = this.cleanSelectedProducts[i].product.internalProducts.reduce((acc, p) => acc + p.billingPrice, 0);

                if (totalInternalProductsCost > 0) {
                    product.product.billingPrice = product.product.internalProducts.reduce((acc, p) => acc + p.billingPrice, 0);
                }
            }
        }

        return cleanSelectedProducts;
    }

    hasPaymentTypeById(id: string) {
        return !!this.payment.paymentTypes.find(paymentType => paymentType.paymentTypeId === id);
    }

    getPackageRelatedProductsByExternalId(externalId: string) {
        return this.packageRelatedProducts[externalId] ?? [];
    }

    getProductsForStep(step: 'passengers' | 'services' | 'payment') {
        return this.products.filter(product => this.productExternalIdsForStep[step].includes(product.product.externalId));
    }

    getCustomBaggageOptionsByFlightDirection(flightDirection: api.CustomBaggageFlightDirection) {
        if (flightDirection && this.selectedFlight
            && this.selectedFlight.customBaggage
            && this.selectedFlight.customBaggage.options
            && this.selectedFlight.customBaggage.options.length) {
            return this.selectedFlight.customBaggage.options?.filter(
                customBaggageOption => customBaggageOption.customBaggageFlightDirection == flightDirection
            );
        } else {
            return [];
        }
    }

    hasSelectedCustomBaggageBySegment(segment: flights.SegmentModel) {
        return this.checkCustomBaggageSelection(
            this.selectedCustomBaggageIds,
            this.getAvailableCustomBaggageItemsByDestinationUIDs(
                '' + segment.departureDestination.uniqueId,
                '' + segment.arrivalDestination.uniqueId
            )
        );
    }

    hasSelectedCustomBaggageBySegmentsBunch(segmentsBunch: flights.SegmentModel[]) {
        const departureDestinationUniqueId = '' + segmentsBunch[0].departureDestination.uniqueId;
        const arrivalDestinationUniqueId = '' + segmentsBunch[segmentsBunch.length - 1].arrivalDestination.uniqueId;

        return this.checkCustomBaggageSelection(
            this.selectedCustomBaggageIds,
            this.getAvailableCustomBaggageItemsByDestinationUIDs(
                departureDestinationUniqueId,
                arrivalDestinationUniqueId
            )
        );
    }

    public getAvailableCustomBaggageItems(): api.CustomBaggageItem[] {
        if (this.selectedFlight) {
            return this.selectedFlight.getAvailableCustomBaggageItems();
        }

        return [];
    }

    public getAvailableCustomBaggageItemsByFlightDirection(flightDirection: api.CustomBaggageFlightDirection): api.CustomBaggageItem[] {
        if (this.selectedFlight) {
            return this.selectedFlight.getAvailableCustomBaggageItemsByFlightDirection(flightDirection);
        }

        return [];
    }

    public getAvailableCustomBaggageItemsByDestinationUIDs(destinationFromUID: string, destinationToUID: string): api.CustomBaggageItem[] {
        if (this.selectedFlight) {
            return this.selectedFlight.getAvailableCustomBaggageItemsByDestinationUIDs(destinationFromUID, destinationToUID);
        }

        return [];
    }

    private checkCustomBaggageSelection(customBaggageItemIds: string[], customBaggageItems: api.CustomBaggageItem[]): boolean {
        return customBaggageItemIds.some(id => customBaggageItems.some(customBaggage => customBaggage.id == id));
    }

    public getConfirmationDataForUndonePopupByPosition(position: api.ProductPopupPosition) {
        const popupAmountBySteps: Record<api.ProductPopupPosition, number> = {
            [api.ProductPopupPosition.afterStep1]: 0,
            [api.ProductPopupPosition.afterStep2]: 0,
            [api.ProductPopupPosition.afterOrder]: 0
        };

        function getOffset(pos: api.ProductPopupPosition) {
            switch (pos) {
                case api.ProductPopupPosition.afterStep2: {
                    return popupAmountBySteps[api.ProductPopupPosition.afterStep1];
                }
                case api.ProductPopupPosition.afterOrder: {
                    return popupAmountBySteps[api.ProductPopupPosition.afterStep1] + popupAmountBySteps[api.ProductPopupPosition.afterStep2];
                }
                default: {
                    return 0;
                }
            }
        }

        const productPopupsWithUniqGroups: ProductPopupModel[] = [];

        for (const productPopup of this.productPopups) {
            if (productPopup.displayType === 'Grouped') {
                const popup = productPopupsWithUniqGroups.find(p => p.brandedKitId === productPopup.brandedKitId);

                if (!popup) {
                    productPopupsWithUniqGroups.push(productPopup);
                }
            } else {
                productPopupsWithUniqGroups.push(productPopup);
            }
        }

        const groupedProductPopups: Record<api.ProductPopupPosition, ProductPopupModel[]> = {
            [api.ProductPopupPosition.afterStep1]: [],
            [api.ProductPopupPosition.afterStep2]: [],
            [api.ProductPopupPosition.afterOrder]: []
        };

        for (const popup of productPopupsWithUniqGroups) {
            if (popup.targetProductId) {
                const product = this.products.find(p => p.product.id === popup.targetProductId);

                if (product) {
                    if (product.product.stepNumber === 1) {
                        groupedProductPopups[api.ProductPopupPosition.afterStep1].push(popup);
                        popupAmountBySteps[api.ProductPopupPosition.afterStep1]++;
                    } else if (product.product.stepNumber === 2) {
                        groupedProductPopups[api.ProductPopupPosition.afterStep2].push(popup);
                        popupAmountBySteps[api.ProductPopupPosition.afterStep2]++;
                    } else if (product.product.stepNumber === 3) {
                        groupedProductPopups[api.ProductPopupPosition.afterOrder].push(popup);
                        popupAmountBySteps[api.ProductPopupPosition.afterOrder]++;
                    }
                }
            } else if (popup.position) {
                groupedProductPopups[popup.position].push(popup);
                popupAmountBySteps[popup.position]++;
            }
        }

        const sortedProductPopups = [
            ...groupedProductPopups[api.ProductPopupPosition.afterStep1],
            ...groupedProductPopups[api.ProductPopupPosition.afterStep2],
            ...groupedProductPopups[api.ProductPopupPosition.afterOrder]
        ];

        const selectedProducts = this.products.filter(p => p.selected);
        const selectedProductIds: number[] = [];

        for (const product of selectedProducts) {
            selectedProductIds.push(product.product.id);

            if (product.product.type === 'Package') {
                selectedProductIds.push(...product.product.internalProducts.map(p => p.id));
            }
        }

        const availableProductPopups = sortedProductPopups.filter(popup => this.products.find(p => p.product.id === popup.productId)).slice(getOffset(position));

        const filteredProductPopups = availableProductPopups.filter(p => {
            return !selectedProductIds.includes(p.productId) && !selectedProductIds.includes(p.targetProductId);
        });

        const truncatedProductPopups: ProductPopupModel[] = [];

        for (const productPopup of filteredProductPopups) {
            if (productPopup.popupId === 0 || (truncatedProductPopups.length < this.maxPopupAmountOnOneStep && this.currentPopupAmount < this.maxPopupAmount)) {
                truncatedProductPopups.push(productPopup);
            }
        }

        const popupForPosition = truncatedProductPopups.find(popup => {
            const notDone = !popup.userInteractionDone;

            if (!popup.targetProductId) {
                return notDone && popup.position == position;
            } else {
                const product = this.products.find(p => p.product.id === popup.targetProductId);
            
                if (product) {
                    if (position === api.ProductPopupPosition.afterStep1 && product.product.stepNumber === 1) {
                        return notDone;
                    } else if (position === api.ProductPopupPosition.afterStep2 && product.product.stepNumber === 2) {
                        return notDone;
                    } else if (position === api.ProductPopupPosition.afterOrder && product.product.stepNumber === 3) {
                        return notDone;
                    }
                }

                return false;
            }
        });

        let confirmationData: (ProductConfirmationData & BaggageConfirmationData & ProductDiscountConfirmationData & ProductServiceBrand) | undefined;
        
        if (popupForPosition) {
            popupForPosition.userInteractionDone = true;
            this.currentPopupAmount++;
        }

        if (popupForPosition?.displayType == 'Grouped') {
            //тут надо задать confirmationData как это сделано ниже
            confirmationData = this.getProductConfirmationDataForBrand(popupForPosition);
            if (confirmationData?.services?.filter(Boolean).length) {
                return confirmationData;
            }

            return 'skipPopupAndGo';
        }
        

        if (popupForPosition?.type == api.ProductPopupType.product) {
            confirmationData = this.getProductConfirmationDataForPopup(popupForPosition);
            if (confirmationData?.product) {
                return confirmationData;
            }

            return 'skipPopupAndGo';
        }
        if (popupForPosition?.type == api.ProductPopupType.discount || popupForPosition?.type == api.ProductPopupType.gift) {
            confirmationData = this.getProductDiscountConfirmationDataForPopup(popupForPosition);
            if (confirmationData?.productDiscount) {
                return confirmationData;
            }

            return 'skipPopupAndGo';
        }
        if (popupForPosition?.type == api.ProductPopupType.flightBaggage) {
            confirmationData = this.getBaggageConfirmationDataForPopup(popupForPosition);
            if (confirmationData?.baggageData) {
                return confirmationData;
            }

            return 'skipPopupAndGo';
        }

        return confirmationData;
    }

    public getProductDiscountConfirmationDataForPopup(productPopup: api.ProductPopup): ProductDiscountConfirmationData | undefined {
        const productToConfirm = this.products.find(
            product =>
                product.product.discountRuleId === productPopup.discountId
                && product.product.id === productPopup.productId
                && product.choiceType == api.ProductChoiceType.notSet
                && !product.selected       
        );
        const secondProduct = this.products.find(
            product =>
                product.product.id === productPopup.secondProductId && productPopup.type !== 'Gift'
        );

        if (productToConfirm && this.selectedProducts.length === 0 || secondProduct) {
            return {
                productDiscount: productToConfirm,
                popup: productPopup
            };
        }

        return undefined;
    }

    public getProductConfirmationDataForPopup(productPopup: api.ProductPopup): ProductConfirmationData | undefined {
        const productToConfirm = this.products.find(
            product =>
                product.product.id === productPopup.productId
                && product.choiceType == api.ProductChoiceType.notSet
                && !product.selected
        );

        if (productToConfirm) {
            return {
                product: productToConfirm,
                popup: productPopup
            };
        }

        return undefined;
    }

    public getProductConfirmationDataForBrand(productPopup: api.ProductPopup): ProductServiceBrand | undefined {
        if (this.forwardKits) {
            for (const element of this.forwardKits) {
                for (const item of element.products) {
                    const res = this.selectedProducts.find(n => n.product.id == item.productId || n.product.internalProductIds?.includes(item.productId));

                    if (res) {
                        element.wasShown = true;
                    }
                }
            }
        }
        
        const productToConfirm = this.forwardKits?.filter(kit => !kit.wasShown);
        
        if (productToConfirm) {
            return {
                services: productToConfirm,
                popup: productPopup
            };
        }

        return undefined;
    }

    private getCheapestCustomBaggageItemsWithSameParams(): api.CustomBaggageItem[] {
        const availableCustomBaggageItems = this.getAvailableCustomBaggageItems()
            .sort((baggageOptionA, baggageOptionB) => baggageOptionA.price - baggageOptionB.price)
            .filter(baggageOption => baggageOption.price > 0);
        let baggageOptionsWithSameParams: api.CustomBaggageItem[] = [];

        if (this.selectedFlight
            && this.selectedFlight.customBaggage.options
            && this.selectedFlight.customBaggage.options.length) {
            availableCustomBaggageItems.some((availableBaggageItem) => {

                this.selectedFlight!.customBaggage.options!.forEach((option) => {
                    const baggageOptionWithSameParams = option.baggageOptions.find(baggageItem =>
                        baggageItem.bagsQuantity == availableBaggageItem.bagsQuantity
                        && baggageItem.bagsWeight == availableBaggageItem.bagsWeight
                        && baggageItem.currency == availableBaggageItem.currency);

                    if (baggageOptionWithSameParams) {
                        baggageOptionsWithSameParams.push(baggageOptionWithSameParams);
                    }
                });

                const cheapestBaggageOptionsWithSameParamsHasFound =
                    baggageOptionsWithSameParams.length == this.selectedFlight!.customBaggage.options!.length;

                if (cheapestBaggageOptionsWithSameParamsHasFound) {
                    return true;
                } else {
                    baggageOptionsWithSameParams = [];

                    return false;
                }
            });
        }

        return baggageOptionsWithSameParams;
    }

    public getCheapestBaggageUpgradeMethod(): {
        method: 'brand' | 'customBaggage';
        price: number;
        firstFlightWithBaggage?: flights.FlightModel;
        baggageItemsWithSameParams?: api.CustomBaggageItem[];
    } | null {
        if (this.selectedFlight) {
            const firstFlightWithBaggage = this.flights.find(x => !x.hasNoBaggage);
            let priceUpgradeWithBrand = 0;

            if (firstFlightWithBaggage) {
                priceUpgradeWithBrand = firstFlightWithBaggage.billingPrice - this.selectedFlight.billingPrice;
            }

            const baggageItemsWithSameParams = this.getCheapestCustomBaggageItemsWithSameParams();
            let priceUpgradeWithCustomBaggage = 0;

            if (baggageItemsWithSameParams.length) {
                baggageItemsWithSameParams.forEach((baggageItem) => {
                    baggageItem.perPassenger
                        ? priceUpgradeWithCustomBaggage += baggageItem.price * this.passengersForBaggage.length
                        : priceUpgradeWithCustomBaggage += baggageItem.price;
                });
            }

            if (priceUpgradeWithBrand > 0
                && (priceUpgradeWithBrand <= priceUpgradeWithCustomBaggage
                    || priceUpgradeWithCustomBaggage == 0)) {
                return {
                    method: 'brand',
                    price: priceUpgradeWithBrand,
                    firstFlightWithBaggage
                };
            } else if (priceUpgradeWithCustomBaggage > 0
                && (priceUpgradeWithCustomBaggage <= priceUpgradeWithBrand
                    || priceUpgradeWithBrand == 0)) {
                return {
                    method: 'customBaggage',
                    price: priceUpgradeWithCustomBaggage,
                    baggageItemsWithSameParams
                };
            } else {
                return null;
            }
        }

        return null;
    }

    public getBaggageValue(baggageParams: {
        pieces: number;
        pieceWeight: number;
    }) {
        let baggage = '';

        if (baggageParams && (baggageParams.pieceWeight === 0 && baggageParams.pieces === 0)) {
            return i18next.t('label_baggage_without_short');
        }

        if (baggageParams) {
            if (baggageParams.pieceWeight > 0) {
                baggage = baggageParams.pieceWeight + G2S.nbsp + i18next.t('label_kg');

                if (baggageParams.pieces > 1) {
                    baggage = baggageParams.pieces + G2S.nbsp + 'x' + G2S.nbsp + baggage;
                }
            } else {
                if (baggageParams.pieces > 0) {
                    baggage = i18next.t('numerous_baggage_piece', { count: baggageParams.pieces });
                }
            }
        }

        return baggage;
    }

    public getConfirmBaggage(productPopup: api.ProductPopup): ConfirmBaggageData | null {
        if (this.selectedFlight
            && this.selectedFlight.hasNoBaggage
            && !this.hasSelectedAnyCustomBaggage) {

            const cheapestUpgradeMethod = this.getCheapestBaggageUpgradeMethod();

            if (cheapestUpgradeMethod) {
                if (cheapestUpgradeMethod.method == 'brand'
                    && cheapestUpgradeMethod.firstFlightWithBaggage) {
                    return {
                        baggage: this.getBaggageValue({
                            pieces: cheapestUpgradeMethod.firstFlightWithBaggage.legs[0].baggage.pieces,
                            pieceWeight: cheapestUpgradeMethod.firstFlightWithBaggage.legs[0].baggage.pieceWeight
                        }),
                        price: Filters.price(cheapestUpgradeMethod.price, false)
                    };
                } else if (cheapestUpgradeMethod.method == 'customBaggage'
                    && cheapestUpgradeMethod.baggageItemsWithSameParams
                    && cheapestUpgradeMethod.baggageItemsWithSameParams.length) {
                    return {
                        baggage: this.getBaggageValue({
                            pieces: cheapestUpgradeMethod.baggageItemsWithSameParams[0].bagsQuantity,
                            pieceWeight: Number(cheapestUpgradeMethod.baggageItemsWithSameParams[0].bagsWeight)
                        }),
                        price: Filters.price(cheapestUpgradeMethod.price, false)
                    };
                }
            }
        }

        return null;
    }

    public getBaggageConfirmationDataForPopup(productPopup: api.ProductPopup): BaggageConfirmationData | undefined {
        const cheapestUpgradeMethod = this.getCheapestBaggageUpgradeMethod();
        const confirmBaggageData = this.getConfirmBaggage(productPopup);

        console.log('BaggageConfirmationData: cheapestUpgradeMethod=' + cheapestUpgradeMethod + ', brandChoiceType=' + this.brandChoiceType);

        if (cheapestUpgradeMethod) {
            if (cheapestUpgradeMethod.method == 'brand' || cheapestUpgradeMethod.method == 'customBaggage') {
                return {
                    baggageData: confirmBaggageData,
                    popup: productPopup
                };
            }
        }

        return undefined;
    }

    public getBrandGroupsBillingPrice(): number {
        let total = 0;

        this.brandGroups.forEach((brandGroup) => {
            total += this.getFlightBillingPrice(brandGroup.selectedFlightId);
        });

        return total;
    }

    public getBrandGroupsBasePrice(): number {
        let total = 0;

        this.brandGroups.forEach((brandGroup) => {
            total += this.getFlightBasePrice(brandGroup.selectedFlightId);
        });

        return total;
    }

    public getBrandGroupsFee(): number {
        let total = 0;

        this.brandGroups.forEach((brandGroup) => {
            total += this.getFlightFee(brandGroup.selectedFlightId);
        });

        return total;
    }

    public upgradeBaggage() {
        const cheapestUpgradeMethod = this.getCheapestBaggageUpgradeMethod();

        if (cheapestUpgradeMethod) {
            if (cheapestUpgradeMethod.method == 'brand'
                && cheapestUpgradeMethod.firstFlightWithBaggage) {
                this.selectedFlightId = cheapestUpgradeMethod.firstFlightWithBaggage.uniqueId;
            } else if (cheapestUpgradeMethod.method == 'customBaggage'
                && cheapestUpgradeMethod.baggageItemsWithSameParams
                && cheapestUpgradeMethod.baggageItemsWithSameParams.length) {
                const flightBaggageOptions: string[] = [];
                const perPassengerBaggageOptions: string[] = [];

                cheapestUpgradeMethod.baggageItemsWithSameParams.forEach((baggageItem) => {
                    if (baggageItem.perPassenger) {
                        flightBaggageOptions.push('');
                        perPassengerBaggageOptions.push(baggageItem.id);
                    } else {
                        flightBaggageOptions.push(baggageItem.id);
                        perPassengerBaggageOptions.push('');
                    }
                });

                this.baggageOptions = flightBaggageOptions;
                this.passengersForBaggage.forEach((passenger) => {
                    passenger.baggageOptions = (<string[]>[]).concat(perPassengerBaggageOptions);
                });
            }
        }
    }

    public getFlightBillingPrice(flightId: string): number {
        const flight = this.flights.find(f => f.uniqueId == flightId);

        if (flight) {
            return flight.billingPrice;
        } else {
            console.error('getFlightBillingPrice: flight not found ' + flightId);

            return 0;
        }
    }

    public getFlightBasePrice(flightId: string): number {
        const flight = this.flights.find(f => f.uniqueId == flightId);

        if (flight) {
            return flight.basePrice;
        } else {
            console.error('getFlightBasePrice: flight not found ' + flightId);

            return 0;
        }
    }

    public getFlightFee(flightId: string): number {
        const flight = this.flights.find(f => f.uniqueId == flightId);

        if (flight) {
            return flight.fee;
        } else {
            console.error('getFlightBasePrice: flight not found ' + flightId);

            return 0;
        }
    }
    public getDiscountAmount(){
        // let dateNow = Date.now();
        // if(this.couponDiscountAmount && this.couponDataExpiring - dateNow > 0){
        //     return this.couponDiscountAmount;
        // } else{
        //     this.couponDiscountAmount = 0;
        //     return 0;
        // } 
        return this.couponDiscountAmount;
    }
    public getTotalPrice(useCalculatedCleanSelectedProducts = false): number {
        let total = this.selectedFlightBillingPrice;
        total += this.getTotalPlacesPrice();

        total += this.getTotalProductsPrice(useCalculatedCleanSelectedProducts);

        total += this.getTotalSeatsPrice();

        total += this.customBaggagePrice;

        total -= this.getDiscountAmount();
        const pt = this.payment!.selectedPaymentType;

        if (pt) {
            total += this.payment!.selectedPaymentType.commission;
        }
        total -= this.payment!.bonusAmount;

        return total;
    }
    
    public getTotalSeatsPrice(): number {
        let total = 0;
        

        this.passengers.forEach(pass=>{
            if (pass.selectedFlightSeats){
                for (const seat in pass.selectedFlightSeats){
                    if(pass.selectedFlightSeats[seat].price){
                        total += pass.selectedFlightSeats[seat].price;
                    }  
                }
            }
        });

        return total;
    }

    public getTotalPlacesPrice(): number {
        let total = 0;

        if (this.trains.length) {
            this.trains.forEach((train) => {
                train.selectedCoach.selectedPlacesQueue.forEach((place) => {
                    total += place.fare.amount;
                });
            });
        }

        return total;
    }

    public getTotalProductsPrice(useCalculatedCleanSelectedProducts = false): number {
        let total = 0;

        if (useCalculatedCleanSelectedProducts) {
            this.calculatedCleanSelectedProducts.forEach(p => {
                total += this.getProductPrice(p);
            });
        } else {
            this.selectedProducts.forEach(p => {
                total += this.getProductPrice(p);
            });
        }

        return total;
    }

    public hasSegmentAvaliableSeatMaps(flightCode: string) {
        for (let seatMapIndex = 0; seatMapIndex < this.seatMaps.length; seatMapIndex++) {
            const seatMap = this.seatMaps[seatMapIndex];
            if (seatMap.segmentFlightCode == flightCode) {
                for (let i = 0; i < seatMap.rows.length; i++) {
                    for (let index = 0; index < seatMap.rows[i].seats.length; index++) {
                        if (seatMap.rows[i].seats[index].available && seatMap.rows[i].seats[index].price > 0) {
                            return true;
                        }
                    }
                }
            }   
        }
        
        return false;
    }

    public getChepestSeatInFlight(flightId: string) {
        const prices: number[] = [];
        
        this.flights.forEach(flight => {
            if (flight.uniqueId === this.selectedFlightId) {
                flight.seatMaps?.forEach(seatMap => {
                    if (seatMap.segmentFlightCode == flightId) {
                        seatMap.rows.forEach(row => {
                            row.seats.forEach(seat => {
                                if (seat.price > 0) {
                                    prices.push(seat.price);
                                }
                            });
                        });
                    }
                });
            }
        });

        return Math.min(...prices);
    }

    public getMoneywallPrice() {
        return utils.Payment.calcMoneyWallPrice(this.getTotalPrice(), this.selectedFlight!.legs[0].segments[0].departureDate);
    }

    public getMoneywallTotalPrice() {
        return this.getMoneywallPrice() * 3;
    }

    public getPassengersCount(): flights.PassengersCount {
        return {
            adults: this.passengers.filter(passenger => passenger.type == api.PassengerType.adult).length,
            children: this.passengers.filter(passenger => passenger.type == api.PassengerType.child).length,
            infants: this.passengers.filter(passenger => passenger.type == api.PassengerType.infant).length
        };
    }

    public getProductPrice(product: ProductModel): number {
        return product.product.billingPrice;
    }

    public getProductByExternalId(externalId: string): ProductModel {
        return utils.find(this.products,
            (p: ProductModel) => {
                return p.product.externalId === externalId;
            });
    }

    public deleteOutOfStockFlight(outOfStockFlightId: string) {
        // Dot not delete last flight
        if (this.flights.length > 1) {
            let deleteFlightIndex: number | null = null;

            for (let i = 0; i < this.flights.length; i++) {
                if (this.flights[i].uniqueId === outOfStockFlightId) {
                    deleteFlightIndex = i;
                    break;
                }
            }

            // Select another flight if required
            if (deleteFlightIndex && deleteFlightIndex >= 0) {
                if (this.flights[deleteFlightIndex].uniqueId === this.selectedFlightId) {
                    for (let i = 0; i < this.flights.length; i++) {
                        if (this.flights[i].uniqueId !== this.flights[deleteFlightIndex].uniqueId) {
                            this.selectedFlightId = this.flights[i].uniqueId;
                            this.brandChoiceType = api.FlightBrandChoiceType.notSet;
                            break;
                        }
                    }
                }

                // Delete out of stock flight
                this.flights.splice(deleteFlightIndex, 1);
            }
        }
    }

    private buildPassengers(
        passengerCount: number,
        passengerType: api.PassengerType,
        citizenship: number,
        documentValidTillEnabled: boolean,
        validationConstraints: PassengerValidationConstraints
    ) {
        for (let i = 0; i < passengerCount; i++) {
            const passenger = new PassengerModel();

            passenger.index = this.passengers.length;
            passenger.type = passengerType;
            passenger.validationConstraints = validationConstraints;
            passenger.citizenship = citizenship;
            passenger.documentValidTillEnabled = documentValidTillEnabled;
            this.passengers.push(passenger);
        }
    }

    private buildPassengerConstraints(reservationConstraints: api.ReservationConstraints, passengerType: string): PassengerValidationConstraints {
        const result = new PassengerValidationConstraints();

        result.allowCyrillicDocumentNumber = reservationConstraints.allowCyrillicDocumentNumber;
        result.allowCyrillicName = reservationConstraints.allowCyrillicPassengerName;
        
        result.middleNameRequired = reservationConstraints.middleNameRequired;
        result.mileCardVisible = reservationConstraints.mileCardVisible;
        result.minDocumentValidTill = moment(reservationConstraints.lastFlightDate);
        result.requiredCitizenshipCountries = reservationConstraints.requiredCitizenshipCountries;
        result.orderFormNewDocumentNumberRuValidationEnabled = reservationConstraints.orderFormNewDocumentNumberRuValidationEnabled;
        result.documentPassportForeign = reservationConstraints.documents ? utils.find(reservationConstraints.documents,
            (item: api.ReservationDocuments) => item.nameDocument === 'PassportForeign') : null;
        result.documentPassportRu = reservationConstraints.documents ? utils.find(reservationConstraints.documents,
            (item: api.ReservationDocuments) => item.nameDocument === 'PassportRu') : null;
        result.documentBirthCertificate = reservationConstraints.documents ? utils.find(reservationConstraints.documents,
            (item: api.ReservationDocuments) => item.nameDocument === 'BirthCertificate') : null;
        result.documentOtherDocument = reservationConstraints.documents ? utils.find(reservationConstraints.documents,
            (item: api.ReservationDocuments) => item.nameDocument === 'OtherDocument') : null;
        
        if (Array.isArray(reservationConstraints.citizenshipConstraints)) {
            result.isRequiredCitizenshipConstrains = reservationConstraints.citizenshipConstraints.some((citizenship) => {
                return citizenship.type.toLowerCase() === 'required';
            });
        } else {
            result.isRequiredCitizenshipConstrains = false;
        }

        result.countriesCitizenshipConstrain = reservationConstraints.citizenshipConstraints 
            ? reservationConstraints
                .citizenshipConstraints.reduce((countries: number[], value) => {
                    countries.push(value.countryId);
                    return countries;
                }, [])
            : [];
        

        switch (passengerType) {
            case 'adult':
                result.maxDateOfBirth = moment(reservationConstraints.maxAdultDateOfBirth);
                result.minDateOfBirth = moment(reservationConstraints.minAdultDateOfBirth);
                break;
            case 'child':
                result.maxDateOfBirth = moment(reservationConstraints.maxChildDateOfBirth);
                result.minDateOfBirth = moment(reservationConstraints.minChildDateOfBirth);
                break;
            case 'infant':
                result.maxDateOfBirth = moment(reservationConstraints.maxInfantDateOfBirth);
                result.minDateOfBirth = moment(reservationConstraints.minInfantDateOfBirth);
                break;
        }

        return result;
    }
    
    public resetAllSelectedSeats() {
        if (Object.keys(this.flatSelectedSeats)?.length) {
            this.passengers.forEach(pass => {
                for (const seat in pass.selectedFlightSeats) delete pass.selectedFlightSeats[seat];
            });
            for (const seat in this.flatSelectedSeats) delete this.flatSelectedSeats[seat];
        }
    }

    public resetAllSelectedBaggage() {
        if (this.selectedFlight
            && this.selectedFlight.customBaggage
            && this.selectedFlight.customBaggage.options
            && this.selectedFlight.customBaggage.options.length) {
            const newBaggageIdsPerPassenger: string[] = [];
            const newBaggageIds: string[] = [];

            this.selectedFlight.customBaggage.options.forEach((option) => {
                if (option.baggageOptions && option.baggageOptions.length) {
                    const selectedOption = option.baggageOptions.find(x => x.selected);
                    if (option.baggageOptions[0].perPassenger) {
                        newBaggageIdsPerPassenger.push(selectedOption ? selectedOption.id : option.baggageOptions[0].id);
                        newBaggageIds.push('');
                    } else {
                        newBaggageIdsPerPassenger.push('');
                        newBaggageIds.push(selectedOption ? selectedOption.id : option.baggageOptions[0].id);
                    }
                } else {
                    newBaggageIds.push('');
                    newBaggageIdsPerPassenger.push('');
                }
            });
            this.passengersForBaggage.forEach((passenger) => {
                passenger.baggageOptions = (<string[]>[]).concat(newBaggageIdsPerPassenger);
            });
            this.baggageOptions = newBaggageIds;
        }
    }

    public selectCustomBaggageByQueryParam(value: string) {
        // [places]-[weight]-[price]
        if (!/^\d*-\d*-\d*$/.test(value)) { return; }


        if (this.selectedFlight
            && this.selectedFlight.customBaggage
            && this.selectedFlight.customBaggage.options
            && this.selectedFlight.customBaggage.options.length) {
            const paramParts = value.split('-');
            const newBaggageIdsPerPassenger: string[] = [];
            const newBaggageIds: string[] = [];

            this.selectedFlight.customBaggage.options.forEach((option) => {
                if (option.baggageOptions && option.baggageOptions.length) {
                    const matchedBaggageOption = option.baggageOptions.find(
                        baggageOption => baggageOption.bagsQuantity == Number(paramParts[0])
                            && baggageOption.bagsWeight == paramParts[1]
                            && Math.floor(baggageOption.price) <= Number(paramParts[2])
                    );

                    if (option.baggageOptions[0].perPassenger) {
                        newBaggageIdsPerPassenger.push(
                            matchedBaggageOption
                                ? matchedBaggageOption.id
                                : option.baggageOptions[0].id
                        );
                        newBaggageIds.push('');
                    } else {
                        newBaggageIdsPerPassenger.push('');
                        newBaggageIds.push(
                            matchedBaggageOption
                                ? matchedBaggageOption.id
                                : option.baggageOptions[0].id
                        );
                    }
                } else {
                    newBaggageIds.push('');
                    newBaggageIdsPerPassenger.push('');
                }
            });
            this.passengersForBaggage.forEach((passenger) => {
                passenger.baggageOptions = (<string[]>[]).concat(newBaggageIdsPerPassenger);
            });
            this.baggageOptions = newBaggageIds;
        }
    }

    static getTicketUrl(reservation: api.ReservationInfo, passenger: api.PassengerInfo): string {
        return `/go/flights/tickets/${reservation.reservationPublicId}/${passenger.passengerId}`;
    }

    static getAllTicketsUrl(reservation: api.ReservationInfo): string {
        return `/go/flights/tickets/${reservation.reservationPublicId}`;
    }

    static getReceiptUrl(reservation: api.ReservationInfo): string {
        return `/go/flights/receipt/${reservation.reservationPublicId}`;
    }

    getMarkerOwner() {
        return utils.Cookies.getMarkerOwner();
    }
}

export class ContactsModel {
    email = '';
    phone = '';

    constructor(contactEmail: string, contactPhone: string) {
        this.email = contactEmail;
        this.phone = contactPhone;
    }
}

export class PaymentTypeModel {
    paymentTypeId: string;
    kind: string;
    commission: number;
    isDirectPayment: boolean;
    paymentTypeOptions: any;
    selected: boolean;

    constructor(paymentType: api.PaymentTypeDesc) {
        this.paymentTypeId = paymentType.paymentTypeId;
        this.kind = paymentType.kind;
        this.commission = paymentType.commission;
        this.isDirectPayment = paymentType.isDirectPayment;
        this.paymentTypeOptions = paymentType.paymentTypeOptions;
        this.selected = false;
    }
}
